<template>

  <v-container fluid fill-height ma-0 pa-1 >

    <v-layout column fill-height pa-0>

      <loading-circle
        :show="loader"
      />

      <v-flex xs12 px-2>

        <v-layout column fill-height justify-start ma-0 pa-0 >
          <v-layout shrink @click.stop="" justify-space-between class="white" px-0 mx-0>
            <v-layout shrink justify-start align-center px-3>
              <span class="text-uppercase">
                {{ $t(namePath) }}
              </span>
            </v-layout>
            <v-layout shrink justify-end align-center>
              <v-tooltip dark color="secondary" bottom>
                <v-btn :data-testid="$options.name + 'v-btn' + 'generatePDF'" small block color="secondary" slot="activator" text @click.stop="generatePDF">{{$t('PDF')}}</v-btn>
                <span>{{ $t('download PDF') }}</span>
              </v-tooltip>

              <v-tooltip dark color="secondary" bottom>
                <v-btn :data-testid="$options.name + 'v-btn' + 'csvExport'" small color="secondary" slot="activator" text @click.stop="csvExport">{{$t('CSV')}}</v-btn>
                <span>{{ $t('download CSV') }}</span>
              </v-tooltip>

              <v-btn :data-testid="$options.name + 'v-btn' + 'showTable--showTable'" @click.stop="showTable = !showTable" flat x-small dark class="white" slot="activator">
                <v-icon v-if="!showTable" color="accent">expand_less</v-icon>
                <v-icon v-if="showTable" color="accent">expand_more</v-icon>
              </v-btn>
            </v-layout>
          </v-layout>

            <v-layout fill-height v-show="showTable" ref="service_table" >

              <scroll-table
                :data="getTable"
                :options="getOptions"
                :headUppercase="false"
                :sortable="true"
                :sorted="sort"
                :translate="true"
                @value="(row, item) => onSelectValue(row, item)"
                @sort="(item) => sortTable(item)"
              />

            </v-layout>

          <v-layout shrink @click.stop="showUnits = !showUnits" justify-space-between class="white" px-0 mx-0>
            <v-layout shrink justify-start align-center px-3>
              <span class="text-uppercase">
                {{ $t('installation units') }}
              </span>
            </v-layout>
            <v-layout shrink justify-end align-center>
              <v-btn :data-testid="$options.name + 'v-btn' + 'showUnits--showUnits'" @click.stop="showUnits = !showUnits" flat x-small dark class="white" slot="activator">
                <v-icon v-if="!showUnits" color="accent">expand_less</v-icon>
                <v-icon v-if="showUnits" color="accent">expand_more</v-icon>
              </v-btn>
            </v-layout>
          </v-layout>

          <v-layout shrink v-show="showUnits">
            <v-flex shrink pb-2>
              <v-text-field
                v-if="data.usize"
                :value="data.usize"
                light
                auto-grow
                outline
                flat
                readonly
                class="white"
                color="primary"
                background-color="white"
                hide-details
                :label="$t('usize')"
                rows="1"
              ></v-text-field>
            </v-flex>

            <v-flex shrink pb-2>
              <v-text-field
                v-if="closedUnits"
                v-model="closedUnits.length"
                light
                auto-grow
                outline
                flat
                readonly
                class="white"
                color="primary"
                background-color="white"
                hide-details
                :label="$t('busy units')"
                rows="1"
              ></v-text-field>
            </v-flex>

            <v-flex shrink pb-2>
              <v-text-field
                v-if="data.usize && closedUnits.length"
                :value="data.usize - closedUnits.length"
                light
                auto-grow
                outline
                flat
                readonly
                class="white"
                color="primary"
                background-color="white"
                hide-details
                :label="$t('available units')"
                rows="1"
              ></v-text-field>
            </v-flex>
          </v-layout> 

        </v-layout>

      </v-flex>

    </v-layout>

  </v-container>

</template>

<script lang="ts">
import Vue from 'vue'
import loadingCircle from '@/nms/components/partials/loader.vue'
import scrollTable from '@/nms/components/partials/scrolltable.vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import myFont from '@/nms/assets/RobotoCondensed-Regular-normal.js'

// Типы
interface TableColumn {
  title: string
  value: string
  dataKey: string
  type: string
  width: string
  time_format?: string
}

interface TableRow {
  position?: number
  maintenance_company_name?: string
  name?: string
  device_type?: string
  device_size?: number
  mount?: string
  warranty_expires?: Date | string
  maintenance_date?: Date | string
  life_cycle_expires?: Date | string
  maintenance_company?: any
  device?: string
  $id?: string
  serial_number?: string
  crumbs?: string
  [key: string]: any
}

interface TableData {
  columns: TableColumn[]
  rows: TableRow[]
}

interface OptionsColumns {
  [key: string]: { width: string }
}

interface OptionsData {
  columns: OptionsColumns
}

interface CabinetData {
  $id?: string
  id?: string
  name?: string
  cls?: string
  type?: string
  [key: string]: any
}

interface DeviceChild {
  $id?: string
  cls?: string
  name?: string
  height?: number
  position?: number
  mount?: string
  device_type?: string
  serial_number?: string
  warranty_expires?: Date | string
  life_cycle_expires?: Date | string
  maintenance_date?: Date | string
  refs?: Array<{ ref?: string; name?: string; [key: string]: any }>
  children?: DeviceChild[]
  [key: string]: any
}

interface CmdbQueryResponse {
  namePath?: string
  links?: DeviceChild[]
  [key: string]: any
}

interface HeapDataOptions {
  arr: DeviceChild[]
  data: CabinetData
  parent: string
  resultArr: TableRow[]
  subscribeArr: DeviceChild[]
}

export default Vue.extend({
  name: 'CABINET_SERVICE_TABLE',

  components: {
    loadingCircle,
    scrollTable,
  },

  props: {
    data: {
      type: Object as () => CabinetData,
      default: () => ({})
    },
    devices: {
      type: Array as () => any[],
      default: () => []
    },
    closedUnits: {
      type: Array as () => any[],
      default: () => []
    },
  },

  data(): {
    loader: boolean
    showTable: boolean
    showUnits: boolean
    options: OptionsData
    filter: any | null
    search: string
    sort: string
    namePath: string
    table: TableData
    statuses?: Record<string, string>
  } {
    return {
      loader: false,
      showTable: true,
      showUnits: false,
      options: {
        columns: {
          text: { width: '40%' },
          timestamp: { width: '40%' },
          actions: { width: '40%' },
          endpoint: { width: '40%' },
          check: { width: '20%' },
        },
      },
      filter: null,
      search: '',
      sort: '',
      namePath: '',
      table: {
        columns: [
          {
            title: 'position',
            value: 'position',
            dataKey: 'position',
            type: 'text',
            width: '10%'
          },
          {
            title: 'organization',
            value: 'maintenance_company_name',
            dataKey: 'maintenance_company_name',
            type: 'text',
            width: '20%'
          },
          {
            title: 'device',
            value: 'name',
            dataKey: 'name',
            type: 'text',
            width: '30%'
          },
          {
            title: 'device_type',
            value: 'device_type',
            dataKey: 'device_type',
            type: 'text',
            width: '20%'
          },
          {
            title: 'device_size',
            value: 'device_size',
            dataKey: 'device_size',
            type: 'number',
            width: '20%'
          },
          {
            title: 'mount',
            value: 'mount',
            dataKey: 'mount',
            type: 'text',
            width: '20%'
          },
          {
            title: 'warranty expires',
            value: 'warranty_expires',
            dataKey: 'warranty_expires',
            type: 'timestamp',
            time_format: 'DD.MM.YYYY',
            width: '20%'
          },
          {
            title: 'maintenance date',
            value: 'maintenance_date',
            dataKey: 'maintenance_date',
            type: 'timestamp',
            time_format: 'DD.MM.YYYY',
            width: '20%'
          },
          {
            title: 'life cycle expires',
            value: 'life_cycle_expires',
            dataKey: 'life_cycle_expires',
            type: 'timestamp',
            time_format: 'DD.MM.YYYY',
            width: '20%'
          },
        ],
        rows: []
      },
    }
  },

  computed: {
    getTable(): TableData {
      const t = this.$merge({}, this.table) as TableData

      if (this.search.length) {
        const searchLower = this.search.toLowerCase()
        t.rows = t.rows.filter(row => {
          return (row.name && row.name.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.maintenance_company_name && row.maintenance_company_name.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.crumbs && row.crumbs.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.device_type && row.device_type.toLowerCase().indexOf(searchLower) > -1)
        })
      }

      if (this.sort) {
        const isDescending = this.sort.charAt(0) === '-'
        const fieldName = isDescending ? this.sort.slice(1) : this.sort
        const direction = isDescending ? -1 : 1
        t.rows = [...t.rows].sort(this.$compare(fieldName, direction))
      }

      return t
    },

    getOptions(): OptionsData {
      return this.options
    },

    getData(): any {
      return this.data
    },
  },

  watch: {
    data: {
      handler(): void {
        this.setup()
      },
      deep: true
    },
  },

  created(): void {
    this.setup()
  },

  methods: {
    setup(): void {
      this.reset()
      this.loader = true
      this.statusUnsubscribe('service-table-equipment')
      this.prepareTable()
    },

    onSelectValue(row: TableRow, item: TableColumn): void {
      if (item.title === 'device' && row.device) {
        this.$emit('device', row.$id)
      }
      if (item.title === 'organization' && row.maintenance_company) {
        this.$emit('organization', row.maintenance_company)
      }
    },

    sortTable(item: TableColumn): void {
      if (this.sort === item.value) {
        this.sort = '-' + item.value
      } else {
        this.sort = item.value
      }
    },

    async prepareTable(): Promise<void> {
      try {
        const query = {
          id: [this.data.$id],
          data: {
            namePath: true,
            links: {
              flatten: false,
              data: {
                refs: true,
                filter: { ref: 'maintenance_company' },
                namePath: true,
              }
            }
          }
        }
        const response = await this.$bridge.cmdbQuery(query) as CmdbQueryResponse[]
        const obj = response[0]

        this.namePath = obj.namePath?.split('/').slice(2).join('/') || ''
        const locationTree = obj.links || []
        const arr: TableRow[] = []
        const subscribeArr: DeviceChild[] = []

        const heapData = async (items: DeviceChild[], parentData: CabinetData, parentPath: string): Promise<void> => {
          const currentParent = parentPath || ''

          for (const child of items) {
            if (child.cls === 'device') {
              const row: TableRow = {}
              let maintenanceCompany

              if (child.refs?.length) {
                maintenanceCompany = child.refs.find(ref => ref.ref === 'maintenance_company')
                if (maintenanceCompany) {
                  row.maintenance_company = maintenanceCompany
                  row.maintenance_company_name = maintenanceCompany.name
                }
              }

              row.device = child.name
              row.$id = child.$id
              row.name = child.name
              row.serial_number = child.serial_number || ''
              row.device_type = child.device_type
              row.device_size = Math.ceil((child.height || 0) / 45) || undefined
              row.warranty_expires = child.warranty_expires
              row.life_cycle_expires = child.life_cycle_expires
              row.maintenance_date = child.maintenance_date
              row.mount = child.mount
              row.position = child.position

              arr.push(row)
              subscribeArr.push(child)
            }

            if (child.children?.length) {
              const newParent = currentParent + parentData.name + '/'
              await heapData(child.children, child as CabinetData, newParent)
            }
          }
        }

        await heapData(locationTree, this.data, '')
        this.table.rows = arr.sort(this.$compare('position', -1))
        this.loader = false
        this.statusSubscribe(subscribeArr, 'service-table-equipment')
      } catch (err) {
        console.error('CATCH', this.$options.name, 'prepareTable', err)
        this.loader = false
      }
    },

    async statusSubscribe(arr: DeviceChild[], group: string): Promise<void> {
      this.$store.dispatch('measuresSubscribeInit', {
        name: 'addStatus',
        group: group,
        listener: 'status'
      })

      if (arr.length) {
        arr.forEach(item => {
          if (item.$id) {
            this.$store.dispatch('addStatusKey', item)
          }
        })
        await this.$bridge.subscribe(arr.map(obj => obj.$id))
      }
    },

    subscribeHandler(e: { $id?: string; [key: string]: any }): void {
      if (e.$id) {
        this.$store.dispatch('addStatus', e)
      }
    },

    statusUnsubscribe(group: string): void {
      this.$store.dispatch('measuresSubscribeClose', {
        handler: this.subscribeHandler,
        group: group,
        listener: 'status'
      })
    },

    generatePDF(): void {
      this.$generatePDF(
        this.getTable,  // объект таблицы с rows и columns
        this.getData,      // или объект вида {cls: String || '', type: String || '', name: String || ''}
        null,           // caller (опционально)
        {
          orientation: 'landscape',  // альбомная ориентация
          format: 'a4',              // формат A4
          font: 'MyFont'             // кастомный шрифт
        }
      )
      return
    },

    csvExport(): void {
      this.$csvExport(this.getTable, 'cabinet_service', this.$options.name)
      return
    },

    getFormattedDate(rows: TableRow[]): TableRow[] {
      const result: TableRow[] = []
      rows.forEach(row => {
        const keys = Object.keys(row)
        keys.forEach(key => {
          if (row[key] instanceof Date) {
            row[key] = (this as any).$moment(row[key]).format('DD MM YYYY')
          }
        })
        result.push(row)
      })
      return result
    },

    reset(): void {
      this.table.rows = []
      this.statuses = {}
      this.showTable = true
      this.showUnits = false
    },
  },
})
</script>

<style>
</style>
